import { createApi, BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { AxiosRequestConfig, AxiosError, Method } from 'axios'
import { HttpService } from '@afterpaytouch/portal-api/api'
import { Money, PaymentType } from '@afterpay/types'

// Create Checkout API Types
export interface AppleGatewayCheckoutRequest {
  sessionId: string
  verifier: string
  checkoutAmount?: Money // Optional: For in-store flows where user adjusts amount
}

export interface AppleGatewayCheckoutResponse {
  token: string
  expires: string
  redirectCheckoutUrl: string
  appleRedirectLink: string
}

// Save Offer API Types
export type UserCohort = 'AFTERPAY' | 'CASHAPP'

export type Period = 'daily' | 'monthly'

export type DeclineCode = 'INSUFFICIENT_CREDIT_LIMIT' | 'ACCOUNT_RESTRICTED' | 'USER_NOT_ELIGIBLE' | 'RISK_DECLINE' | 'FRAUD_DECLINE'

export interface DownPayment extends Money {
  dueBy: string
}

export interface InterestDetails {
  aprInterest: {
    percentage: string
    totalInterest: Money
  }
}

export interface PresentedOffer {
  offerId: string
  numberOfInstallments: number
  recurringInstallmentAmount: Money
  offerType: PaymentType.PBI | PaymentType.PCL
  period: Period
  total: Money
  interval: number
  downPayment: DownPayment
  firstInstallmentDueDate: string
  finalInstallmentAmount: Money
  interestDetails?: InterestDetails
}

export interface OfferDetails {
  presentedOffer: PresentedOffer[]
  selectedOfferId: string
  selectedOfferLink: string
}

export interface AppleGatewaySaveOfferRequestAccepted {
  token: string
  userWasDeclined: false
  userCohort: UserCohort
  userId: string
  offerDetails: OfferDetails
  phoneNumber: string
  validUntil?: string
}

export interface AppleGatewaySaveOfferRequestDeclined {
  token: string
  userWasDeclined: true
  userCohort: UserCohort
  userId: string
  declinedCode: DeclineCode
}

export type AppleGatewaySaveOfferRequest = AppleGatewaySaveOfferRequestAccepted | AppleGatewaySaveOfferRequestDeclined

export interface AppleGatewaySaveOfferResponse {
  appleWalletRedirectLink?: string
}

// Issue Card API Types
export interface Consumer {
  consumerUuid: string
}

export interface AppleGatewayIssueCardRequest {
  token: string
  consumer: Consumer
}

export interface AppleGatewayIssueCardResponse {
  appleWalletRedirectLink?: string
}

// Redirect API Types
export interface AppleGatewayRedirectRequest {
  queryArgs?: string
}

export interface AppleGatewayRedirectResponse {
  redirectUrl: string
}

export class AppleGatewayService {
  public baseQueryFn: HttpService | null = null

  readonly getBaseQuery: BaseQueryFn<
    {
      url: string
      method: Method
      data?: AxiosRequestConfig['data']
      headers?: Record<string, string>
    },
    unknown,
    FetchBaseQueryError,
    {
      timeout?: AxiosRequestConfig['timeout']
    }
  > = async (args, _api, opts = {}) => {
    const { url, method, data, headers } = args
    const { timeout } = opts

    try {
      if (this.baseQueryFn === null) {
        throw new Error('Apple Gateway baseQueryFn not set')
      }
      const result = await this.baseQueryFn.request(url, method, data ?? null, headers, timeout)
      return { data: result }
    } catch (e) {
      const error = e as AxiosError & Error
      const response = error.response?.data ?? error.message ?? null
      return {
        error: {
          status: error.response?.status ?? 'FETCH_ERROR',
          data: response,
        } as FetchBaseQueryError,
      }
    }
  }

  public setBaseQueryFn(queryFn: HttpService): AppleGatewayService {
    this.baseQueryFn = queryFn
    return this
  }

  readonly service = createApi({
    reducerPath: 'appleGatewayApi',
    baseQuery: this.getBaseQuery,
    endpoints: (build) => ({
      createCheckout: build.mutation<AppleGatewayCheckoutResponse, AppleGatewayCheckoutRequest>({
        query: (request) => ({
          url: '/en-US/v1/checkout/create', // Hardcoded en-US for dev/testing - routes to US instance
          method: 'POST',
          data: request,
        }),
      }),
      saveOffer: build.mutation<AppleGatewaySaveOfferResponse, AppleGatewaySaveOfferRequest>({
        query: (request) => ({
          url: '/en-US/v1/checkout/save/offer', // Hardcoded en-US for dev/testing - routes to US instance
          method: 'POST',
          data: request,
        }),
      }),
      issueCard: build.mutation<AppleGatewayIssueCardResponse, AppleGatewayIssueCardRequest>({
        query: (request) => ({
          url: '/en-US/v1/checkout/issue/card', // Hardcoded en-US for dev/testing - routes to US instance
          method: 'POST',
          data: request,
        }),
      }),
      getRedirect: build.query<AppleGatewayRedirectResponse, AppleGatewayRedirectRequest>({
        query: (request) => ({
          url: '/en-US/v1/checkout/redirect', // Hardcoded en-US for dev/testing - routes to US instance
          method: 'POST',
          data: request,
        }),
      }),
    }),
  })
}

export const appleGatewayService = new AppleGatewayService()

/**
 * RTK Query hooks for Apple Gateway Service API
 *
 * @example
 * // Create checkout
 * const [createCheckout, { isLoading, error }] = useCreateCheckoutMutation()
 * const result = await createCheckout({
 *   sessionId: 'session-123',
 *   verifier: 'verifier-abc'
 * })
 * // Returns: { token, expires, redirectCheckoutUrl, appleRedirectLink }
 *
 * @example
 * // Save offer (user accepted)
 * const [saveOffer] = useSaveOfferMutation()
 * await saveOffer({
 *   token: '002.xyz',
 *   userWasDeclined: false,
 *   userCohort: 'AFTERPAY', // or 'CASHAPP'
 *   userId: 'consumer-uuid',
 *   phoneNumber: '18582342487',
 *   offerDetails: {
 *     presentedOffer: [{
 *       offerId: 'offer-123',
 *       numberOfInstallments: 4,
 *       recurringInstallmentAmount: { amount: '25.00', currency: 'USD' },
 *       offerType: 'PBI',
 *       period: 'daily',
 *       total: { amount: '100.00', currency: 'USD' },
 *       interval: 14,
 *       downPayment: { amount: '25.00', currency: 'USD', dueBy: '2024-01-15T15:00:00-08:00' }
 *     }],
 *     selectedOfferId: 'offer-123',
 *     selectedOfferLink: 'https://...'
 *   }
 * })
 *
 * @example
 * // Save offer (user declined)
 * const [saveOffer] = useSaveOfferMutation()
 * await saveOffer({
 *   token: '002.xyz',
 *   userWasDeclined: true,
 *   userCohort: 'AFTERPAY', // or 'CASHAPP'
 *   userId: 'consumer-uuid',
 *   declinedCode: 'INSUFFICIENT_CREDIT_LIMIT'
 * })
 *
 * @example
 * // Issue card
 * const [issueCard] = useIssueCardMutation()
 * const result = await issueCard({
 *   token: '002.xyz',
 *   consumer: {
 *     consumerUuid: 'fd85535c-99c3-47f2-99b2-c8669b7a0198'
 *   }
 * })
 * // Returns: { appleWalletRedirectLink }
 *
 * @example
 * // Get redirect URL (handles Apple Gateway staging auth)
 * const [getRedirect] = useGetRedirectMutation()
 * const result = await getRedirect({
 *   queryArgs: 'code=abc123&state=xyz789'
 * })
 * // Returns: { redirectUrl: 'https://merchant.com/success?orderId=...' }
 */
export const { useCreateCheckoutMutation, useSaveOfferMutation, useIssueCardMutation, useGetRedirectQuery, useLazyGetRedirectQuery } =
  appleGatewayService.service

export const CREATE_CHECKOUT_FIXED_KEY = 'apple-paylater-create-checkout'
